/** Rejilla SKU·parámetro × mes. Magnitud → SECUENCIAL: una sola hue, claro a oscuro.
 *  Un arcoíris aquí haría que "peor" no se leyera como "más oscuro". */
import { useState } from "react";
import type { Grafico, Idioma } from "../lib/tipos";
import { num } from "../lib/formato";
import { t } from "../lib/i18n";
import { ANCHO_MOVIL, Marco, SECUENCIAL, Tooltip, pasoSecuencial, useEsMovil } from "./comun";

const CELDA = 30;
const ALTO_FILA = 26;
const IZQ = 210;

export function GraficoHeatmap({ grafico, idioma }: { grafico: Grafico; idioma: Idioma }) {
  const filas = grafico.filas ?? [];
  const columnas = grafico.columnas ?? [];
  const [activo, setActivo] = useState<{ f: number; c: number } | null>(null);
  const movil = useEsMovil();
  const txt = t(idioma);
  if (!filas.length || !columnas.length) return null;

  // El máximo se calcula sobre lo MEDIDO: un `null` no es un cero y no puede entrar en la escala.
  const max = Math.max(...filas.flatMap((f) => f.valores).filter((v): v is number => v !== null), 1);
  // El peor caso medido de los seis gráficos: con `IZQ = 210` y doce columnas el viewBox salía a
  // 582 px y en un teléfono se renderizaba a escala 0,44 — el texto de 9 px quedaba en 4. En
  // móvil la etiqueta de fila va ENCIMA y las celdas ocupan todo el ancho, que además las hace
  // del mismo tamaño que en escritorio en vez de un tercio.
  const izq = movil ? 0 : IZQ;
  const celda = movil ? Math.floor((ANCHO_MOVIL - 8) / columnas.length) : CELDA;
  const altoEtiqueta = movil ? 16 : 0;
  const altoFila = (movil ? 24 : ALTO_FILA) + altoEtiqueta;
  const cuerpo = movil ? 12 : 9;
  const arriba = movil ? 18 : 34;
  const ancho = izq + columnas.length * celda + (movil ? 8 : 12);
  const alto = arriba + filas.length * altoFila + 8;

  return (
    <>
      <Marco titulo={grafico.titulo} idioma={idioma} ancho={ancho} alto={alto}>
        <svg viewBox={`0 0 ${ancho} ${alto}`} width="100%" height="100%" onMouseLeave={() => setActivo(null)}>
          {/* «Sin medir» va con TRAMA y no con un color claro: en una escala secuencial cualquier
              color pálido se lee como «poco», y lo que hay que decir es «nada que leer». El rayado
              además se distingue sin depender del color. */}
          <defs>
            <pattern id="sin-medir" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <rect width="6" height="6" fill="var(--superficie-2)" />
              <line x1="0" y1="0" x2="0" y2="6" stroke="var(--borde-fuerte)" strokeWidth="1.5" />
            </pattern>
          </defs>
          {columnas.map((c, i) => (
            <text key={c} x={izq + i * celda + celda / 2} y={arriba - 6} textAnchor="middle"
                  fontSize={movil ? 10 : 9} fill="var(--texto-3)">
              {c.slice(-2)}
            </text>
          ))}
          {/* El año va al lado de los meses en escritorio; en móvil no cabe y se pone al final. */}
          <text x={movil ? ancho - 4 : IZQ - 6} y={arriba - 6} textAnchor="end"
                fontSize={movil ? 10 : 9} fill="var(--texto-3)">
            {movil ? columnas[0]?.slice(0, 4) : `${columnas[0]?.slice(0, 4)} →`}
          </text>
          {filas.map((f, fi) => (
            <g key={f.etiqueta}>
              <text x={movil ? 0 : IZQ - 8} y={arriba + fi * altoFila + (movil ? 11 : altoFila / 2)}
                    textAnchor={movil ? "start" : "end"} dominantBaseline={movil ? undefined : "middle"}
                    fontSize={cuerpo} fill="var(--texto-2)">
                {f.etiqueta.length > 34 ? `${f.etiqueta.slice(0, 33)}…` : f.etiqueta}
              </text>
              {f.valores.map((v, ci) => {
                const seleccionada = activo?.f === fi && activo?.c === ci;
                return (
                  <g key={ci}>
                    {/* 2px de separación entre celdas: el hueco de superficie que pide la especificación. */}
                    <rect x={izq + ci * celda + 1} y={arriba + altoEtiqueta + fi * altoFila + 1}
                          width={celda - 2} height={altoFila - altoEtiqueta - 2}
                          rx="3" fill={v === null ? "url(#sin-medir)" : pasoSecuencial(v, max)}
                          stroke={seleccionada ? "var(--texto-1)" : "transparent"} strokeWidth="2"
                          onMouseEnter={() => setActivo({ f: fi, c: ci })} />
                    {v !== null && v > 0 && (
                      <text x={izq + ci * celda + celda / 2}
                            y={arriba + altoEtiqueta + fi * altoFila + (altoFila - altoEtiqueta) / 2}
                            textAnchor="middle" dominantBaseline="middle" fontSize={cuerpo} pointerEvents="none"
                            fill={v / max > 0.55 ? "var(--superficie-0)" : "var(--texto-2)"}>
                        {Math.round(v)}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          ))}
          {activo && filas[activo.f] && (
            <Tooltip x={izq + activo.c * celda} y={arriba + altoEtiqueta + activo.f * altoFila} ancho={ancho}
                     lineas={[`${filas[activo.f]!.etiqueta}`,
                              filas[activo.f]!.valores[activo.c] === null
                                ? `${columnas[activo.c]}: ${txt.sinMedicion}`
                                : `${columnas[activo.c]}: ${num(filas[activo.f]!.valores[activo.c]!, 1)} ${grafico.unidad ?? ""}`]} />
          )}
        </svg>
      </Marco>
      <div className="escala">
        <span>0</span>
        {SECUENCIAL.map((c) => <span key={c} className="escala-paso" style={{ background: c }} aria-hidden="true" />)}
        <span>{num(max, 0)} {grafico.unidad}</span>
      </div>
    </>
  );
}
